/**
 * CodeSection Component
 *
 * Description:
 * Handles academy access code validation and subscription renewal. Renders a form where users can enter access codes to gain access to academy content. Manages code verification, subscription renewal, and provides user feedback through dialog messages.
 *
 * Features:
 * - Code input field with validation and submission functionality.
 * - Asynchronous code verification and subscription renewal process.
 * - Loading states during API requests for better UX.
 * - Success and error dialog messages with appropriate feedback.
 * - Automatic redirection upon successful code validation.
 * - Help center link for additional support.
 * - Translation support for multi-language text.
 * - Integration with Redux for academy data management.
 *
 * Props:
 * (No props required. All state is managed internally and from Redux.)
 *
 * Hooks & State:
 * - useState:
 *   - code: Stores the entered access code value.
 *   - loading: Controls loading state during API requests.
 *   - title: Stores dialog title for success/error messages.
 *   - message: Stores dialog message content for user feedback.
 *   - openDialog: Controls dialog visibility state.
 *   - redirect: Flag to determine if redirection should occur after success.
 * - useDispatch: Dispatches Redux actions for academy banner control.
 * - useSelector: Accesses Redux state for academy information.
 * - useTranslation: Provides translation for UI text elements.
 * - useEffect: Shows academy banner on component mount.
 *
 * Render Flow:
 * 1. On mount, shows the academy banner via Redux action.
 * 2. Renders text field for code input with change handler.
 * 3. Renders submit button (disabled when no code entered).
 * 4. During submission:
 *    - Shows loading spinner
 *    - Validates code with academy API
 *    - If valid, renews subscription and shows success dialog
 *    - If invalid, shows error dialog with help information
 * 5. Renders help section with link to support center.
 * 6. Shows dialog with appropriate success/error feedback.
 *
 * Good Practices / Warnings:
 * - Component uses async/await for better error handling in API calls.
 * - Loading states prevent multiple submissions during processing.
 * - Error handling provides clear feedback to users.
 * - External link opens in new tab for better UX.
 * - Redux state management ensures consistent academy data.
 * - Translation integration supports internationalization.
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import Loading from 'components/UI/Loading/Loading';
import DialogMessage from 'components/UI/DialogMessage/DialogMessage';

import { academyDataActions } from '../../../store/academy-slice';
import { checkCode, renewSuscription } from 'store/academy-actions';

import classes from './CodeSection.module.css';

const CodeSection = () => {
  const { t } = useTranslation(['access']);
  // State for access code input
  const [code, setCode] = useState('');
  // State for loading during API requests
  const [loading, setLoading] = useState(false);
  // State for dialog title and message
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  // State for dialog visibility
  const [openDialog, setOpenDialog] = useState(false);
  // State for redirection flag
  const [redirect, setRedirect] = useState(false);
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);

  // Show academy banner on mount
  useEffect(() => {
    dispatch(
      academyDataActions.showBanner({
        showBanner: true
      })
    );
  }, []);

  /**
   * Handles code input change
   */
  const handleChange = () => {
    setCode(event.target.value);
  };

  /**
   * Handles code submission and validation
   */
  const sendCode = () => {
    setLoading(true);
    (async () => {
      const codeData = await checkCode(
        code,
        dataAcademy.data.idAcademy
      );
      if (codeData) {
        const renewSuscriptionData = await renewSuscription(
          code,
          dataAcademy.data.idAcademy
        );
        if (renewSuscriptionData) {
          setLoading(false);
        }
        setTitle(t('success_code'));
        setMessage('');
        setOpenDialog(true);
        setLoading(false);
        setRedirect(true);
      } else {
        setTitle(t('code_not_valid'));
        setMessage(t('code_not_valid_info'));
        setOpenDialog(true);
        setLoading(false);
      }
    })();
  };

  /**
   * Handles dialog close and redirection
   */
  const handleClose = () => {
    setOpenDialog(false);
    if (redirect) {
      window.location.href = `/${dataAcademy.data.shortName}`;
    }
  };

  return (
    <>
      <aside className={classes.codeSection}>
        <TextField
          id="outlined-textarea"
          label={t('code')}
          className={classes.textFieldCode}
          value={code} // Controlled input value
          onChange={handleChange} // Input change handler
          margin="normal"
          variant="outlined"
        />
        <div className={classes.sendCodeBtnContainer}>
          {!loading ? (
            <Button
              className={`${
                code
                  ? classes.btnSendCode
                  : classes.btnSendCodeDisabled
              }`}
              disabled={!code} // Disable when no code entered
              onClick={sendCode} // Code submission handler
            >
              {t('send')}
            </Button>
          ) : (
            <Loading /> // Loading component during request
          )}
        </div>
      </aside>
      <aside className={classes.aditionalInfoContainer}>
        <p className={classes.aditionalText}>{t('any_doubt')}</p>
        <a
          href="http://ayuda.mayahii.com/"
          target="_blank"
          className={classes.aditionalLink}
          rel="noreferrer"
        >
          {t('help_center')}
        </a>
      </aside>
      <DialogMessage
        title={title} // Dialog title prop
        message={message} // Dialog message prop
        open={openDialog} // Dialog visibility control prop
        handleClose={handleClose} // Dialog close handler prop
      />
    </>
  );
};

export default CodeSection;
